import type { SkillAudience } from '@parcel/types';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Input,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuItem,
  Skeleton,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  cn,
} from '@parcel/ui';
import {
  ChevronDown,
  ChevronRight,
  Compass,
  FileText,
  HardHat,
  Layers,
  MoreHorizontal,
  NotebookText,
  Pencil,
  Plus,
  Search,
  Trash2,
} from 'lucide-react';
import * as React from 'react';
import {
  type SkillCatalogResult,
  type SkillSummary,
  parseSkillError,
} from '../../lib/spec-skills.js';
import { SpecAiMark } from '../app-sidebar.js';
import { Hint } from '../hint.js';
import type { SkillAuthoringBlockReason } from './skill-authoring-launcher.js';
import { skillErrorMessage } from './skill-error-copy.js';
import { RESERVED_SKILL_PATH, normalizeSkillPath } from './skill-files-editor.js';

/**
 * The Skills side panel: one collapsible section per audience, plus the
 * create menu. System skills are hidden until the plan surfaces them.
 */

/** One stable string per skill, for URL state and selection. */
export function skillKeyOf(skill: Pick<SkillSummary, 'locator'>): string {
  return skill.locator.source === 'custom'
    ? `custom:${skill.locator.skillId}`
    : `official:${skill.locator.officialSkillId}`;
}

export function parseSkillKey(
  key: string | null | undefined,
): { source: 'custom'; skillId: string } | { source: 'official'; officialSkillId: string } | null {
  if (!key) return null;
  const [source, id] = key.split(':', 2);
  if (!id) return null;
  if (source === 'custom') return { source: 'custom', skillId: id };
  if (source === 'official') return { source: 'official', officialSkillId: id };
  return null;
}

export interface SkillTreeSection {
  audience: SkillAudience;
  result?: SkillCatalogResult;
  error?: unknown;
  isPending: boolean;
}

export interface SkillTreeProps {
  sections: readonly SkillTreeSection[];
  selectedKey: string | null;
  selectedFilePath: string;
  expandedKey: string | null;
  expandedFiles?: readonly string[];
  expandedEditedFiles?: readonly string[];
  expandedFilesPending: boolean;
  expandedFilesError: boolean;
  query: string;
  onQuery: (next: string) => void;
  onSelect: (skill: SkillSummary) => void;
  onToggle: (skill: SkillSummary) => void;
  onSelectFile: (skill: SkillSummary, path: string) => void;
  onAddFile: (skill: SkillSummary) => void;
  onRenameFile: (skill: SkillSummary, path: string, nextPath: string) => void;
  onDeleteFile: (skill: SkillSummary, path: string) => void;
  editableFileKey?: string | null;
  onCreateManually: () => void;
  onCreateInChat: () => void;
  /** Absent while the catalog has nothing to explore, which hides the button. */
  onExplore?: () => void;
  onRetry: () => void;
  createBlockReason?: SkillAuthoringBlockReason;
  createPending?: boolean;
}

const SECTION_LABEL: Readonly<Record<SkillAudience, string>> = {
  workspace: 'Workspace',
  personal: 'Personal',
};

export function blockCopy(reason: Exclude<SkillAuthoringBlockReason, null>): string {
  if (reason === 'unavailable') return 'Spec is not available, so a chat cannot start.';
  if (reason === 'read_only') return 'Spec is paused, so a chat cannot start.';
  if (reason === 'seat_required') return 'You need an AI seat to create a skill with Spec.';
  return 'This workspace is out of credits, so a chat cannot start.';
}

export function matchesQuery(skill: SkillSummary, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return (
    skill.name.toLowerCase().includes(q) ||
    skill.slug.toLowerCase().includes(q) ||
    skill.description.toLowerCase().includes(q)
  );
}

export function SkillTree({
  sections,
  selectedKey,
  selectedFilePath,
  expandedKey,
  expandedFiles,
  expandedEditedFiles,
  expandedFilesPending,
  expandedFilesError,
  query,
  onQuery,
  onSelect,
  onToggle,
  onSelectFile,
  onAddFile,
  onRenameFile,
  onDeleteFile,
  editableFileKey = null,
  onCreateManually,
  onCreateInChat,
  onExplore,
  onRetry,
  createBlockReason = null,
  createPending = false,
}: SkillTreeProps) {
  return (
    <aside
      data-testid="skill-tree"
      aria-label="Skills"
      className="flex h-full w-72 shrink-0 flex-col border-r bg-background"
    >
      <div className="flex items-center gap-2 px-4 pb-2 pt-4">
        <Layers className="size-4 text-muted-foreground" aria-hidden />
        <h1 className="text-sm font-semibold">Skills</h1>
        <div className="ml-auto flex items-center gap-0.5">
          <TooltipProvider>
            {onExplore ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    className="size-7"
                    aria-label="Explore skills"
                    onClick={onExplore}
                  >
                    <Compass className="size-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Explore skills</TooltipContent>
              </Tooltip>
            ) : null}
          </TooltipProvider>
          <SkillCreateMenu
            blockReason={createBlockReason}
            pending={createPending}
            onCreateManually={onCreateManually}
            onCreateInChat={onCreateInChat}
          >
            <Button
              type="button"
              size="icon"
              variant="ghost"
              className="size-7"
              aria-label="Create skill"
            >
              <Plus className="size-4" />
            </Button>
          </SkillCreateMenu>
        </div>
      </div>

      <div className="px-4 pb-3">
        <SearchPill
          label="Search skills"
          placeholder="Search skills…"
          value={query}
          onChange={onQuery}
        />
      </div>

      <nav className="min-h-0 flex-1 overflow-y-auto px-2 pb-4">
        {sections.map((section) => (
          <TreeSection
            key={section.audience}
            section={section}
            query={query}
            selectedKey={selectedKey}
            selectedFilePath={selectedFilePath}
            expandedKey={expandedKey}
            expandedFiles={expandedFiles}
            expandedEditedFiles={expandedEditedFiles}
            expandedFilesPending={expandedFilesPending}
            expandedFilesError={expandedFilesError}
            onSelect={onSelect}
            onToggle={onToggle}
            onSelectFile={onSelectFile}
            onAddFile={onAddFile}
            onRenameFile={onRenameFile}
            onDeleteFile={onDeleteFile}
            editableFileKey={editableFileKey}
            onRetry={onRetry}
          />
        ))}
      </nav>
    </aside>
  );
}

export function SkillCreateMenu({
  children,
  blockReason,
  pending,
  onCreateManually,
  onCreateInChat,
  align = 'end',
}: {
  children: React.ReactNode;
  blockReason: SkillAuthoringBlockReason;
  pending: boolean;
  onCreateManually: () => void;
  onCreateInChat: () => void;
  align?: 'start' | 'end';
}) {
  const chatDisabled = Boolean(blockReason) || pending;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{children}</DropdownMenuTrigger>
      <DropdownMenuContent align={align} className="w-56" data-testid="skill-create-menu">
        <DropdownMenuItem
          disabled={chatDisabled}
          onSelect={() => onCreateInChat()}
          title={blockReason ? blockCopy(blockReason) : undefined}
        >
          <HardHat className="size-4" />
          Create skill in chat
          <SpecAiMark />
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => onCreateManually()}>
          <NotebookText className="size-4" />
          Manually create
        </DropdownMenuItem>
        {blockReason ? (
          <p className="px-2 pb-1 pt-1 text-xs text-muted-foreground">{blockCopy(blockReason)}</p>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function TreeSection({
  section,
  query,
  selectedKey,
  selectedFilePath,
  expandedKey,
  expandedFiles,
  expandedEditedFiles,
  expandedFilesPending,
  expandedFilesError,
  onSelect,
  onToggle,
  onSelectFile,
  onAddFile,
  onRenameFile,
  onDeleteFile,
  editableFileKey,
  onRetry,
}: {
  section: SkillTreeSection;
  query: string;
  selectedKey: string | null;
  selectedFilePath: string;
  expandedKey: string | null;
  expandedFiles?: readonly string[];
  expandedEditedFiles?: readonly string[];
  expandedFilesPending: boolean;
  expandedFilesError: boolean;
  onSelect: (skill: SkillSummary) => void;
  onToggle: (skill: SkillSummary) => void;
  onSelectFile: (skill: SkillSummary, path: string) => void;
  onAddFile: (skill: SkillSummary) => void;
  onRenameFile: (skill: SkillSummary, path: string, nextPath: string) => void;
  onDeleteFile: (skill: SkillSummary, path: string) => void;
  editableFileKey: string | null;
  onRetry: () => void;
}) {
  const [open, setOpen] = React.useState(true);
  const label = SECTION_LABEL[section.audience];
  const contentId = `skill-tree-${section.audience}`;

  return (
    <section data-testid={`skill-tree-section-${section.audience}`} className="mb-3">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={contentId}
        onClick={() => setOpen((current) => !current)}
        className="flex w-full items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-muted-foreground hover:text-foreground"
      >
        {open ? (
          <ChevronDown className="size-3.5" aria-hidden />
        ) : (
          <ChevronRight className="size-3.5" aria-hidden />
        )}
        {label}
      </button>
      {open ? (
        <div id={contentId} className="mt-0.5">
          <SectionBody
            section={section}
            query={query}
            selectedKey={selectedKey}
            selectedFilePath={selectedFilePath}
            expandedKey={expandedKey}
            expandedFiles={expandedFiles}
            expandedEditedFiles={expandedEditedFiles}
            expandedFilesPending={expandedFilesPending}
            expandedFilesError={expandedFilesError}
            onSelect={onSelect}
            onToggle={onToggle}
            onSelectFile={onSelectFile}
            onAddFile={onAddFile}
            onRenameFile={onRenameFile}
            onDeleteFile={onDeleteFile}
            editableFileKey={editableFileKey}
            onRetry={onRetry}
          />
        </div>
      ) : null}
    </section>
  );
}

function SectionBody({
  section,
  query,
  selectedKey,
  selectedFilePath,
  expandedKey,
  expandedFiles,
  expandedEditedFiles,
  expandedFilesPending,
  expandedFilesError,
  onSelect,
  onToggle,
  onSelectFile,
  onAddFile,
  onRenameFile,
  onDeleteFile,
  editableFileKey,
  onRetry,
}: {
  section: SkillTreeSection;
  query: string;
  selectedKey: string | null;
  selectedFilePath: string;
  expandedKey: string | null;
  expandedFiles?: readonly string[];
  expandedEditedFiles?: readonly string[];
  expandedFilesPending: boolean;
  expandedFilesError: boolean;
  onSelect: (skill: SkillSummary) => void;
  onToggle: (skill: SkillSummary) => void;
  onSelectFile: (skill: SkillSummary, path: string) => void;
  onAddFile: (skill: SkillSummary) => void;
  onRenameFile: (skill: SkillSummary, path: string, nextPath: string) => void;
  onDeleteFile: (skill: SkillSummary, path: string) => void;
  editableFileKey: string | null;
  onRetry: () => void;
}) {
  if (section.error) {
    const parsed = parseSkillError(section.error);
    return (
      <div className="px-4 py-1 text-xs text-muted-foreground">
        <p>{parsed ? skillErrorMessage(parsed) : 'Unable to load skills.'}</p>
        {parsed?.code === 'disabled' ? null : (
          <button type="button" className="mt-1 underline" onClick={onRetry}>
            Try again
          </button>
        )}
      </div>
    );
  }
  if (section.isPending || !section.result) {
    return (
      <div className="space-y-1.5 px-4 py-1" aria-busy="true">
        <Skeleton className="h-4 w-3/4" />
        <Skeleton className="h-4 w-1/2" />
      </div>
    );
  }
  if (section.result.state === 'disabled') {
    return <Muted>Skills are switched off.</Muted>;
  }
  const items = section.result.items.filter((skill) => matchesQuery(skill, query));
  if (items.length === 0) {
    return <Muted>{query.trim() ? 'No matches' : 'No skills'}</Muted>;
  }
  return (
    <ul className="space-y-px">
      {items.map((skill) => {
        const key = skillKeyOf(skill);
        const selected = key === selectedKey;
        const expanded = key === expandedKey;
        const canAddFile = key === editableFileKey;
        const filesId = `skill-tree-files-${key.replace(/[^a-zA-Z0-9_-]/g, '-')}`;
        return (
          <li key={key}>
            <SidebarMenu className="gap-0">
              <SidebarMenuItem>
                <Button
                  type="button"
                  variant="ghost"
                  data-size="default"
                  data-testid="skill-tree-item"
                  data-skill-key={key}
                  aria-current={selected ? 'true' : undefined}
                  aria-expanded={expanded}
                  aria-controls={filesId}
                  onClick={() => {
                    onSelect(skill);
                    onToggle(skill);
                  }}
                  className={cn(
                    'peer/menu-button h-8 w-full justify-start gap-2 px-2 text-left text-sm',
                    canAddFile &&
                      'transition-[padding] group-hover/menu-item:pr-8 group-focus-within/menu-item:pr-8',
                    selected ? 'bg-muted font-medium' : 'text-foreground',
                  )}
                >
                  {expanded ? (
                    <ChevronDown className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />
                  ) : (
                    <ChevronRight className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />
                  )}
                  <Layers className="size-4 shrink-0 text-muted-foreground" aria-hidden />
                  <span className="min-w-0 flex-1 truncate">{skill.name}</span>
                  {skill.lifecycle !== 'published' ? (
                    <span className="shrink-0 text-[11px] font-normal text-muted-foreground">
                      Draft
                    </span>
                  ) : skill.hasUnpublishedChanges ? (
                    <Hint label="Saved edits that are not live yet">
                      <span className="shrink-0 text-[11px] font-normal text-amber-600">
                        Edited
                      </span>
                    </Hint>
                  ) : null}
                </Button>
                {canAddFile ? (
                  <SidebarMenuAction
                    showOnHover
                    type="button"
                    aria-label={`Add file to ${skill.name}`}
                    title="Add file"
                    onClick={() => onAddFile(skill)}
                  >
                    <Plus aria-hidden />
                  </SidebarMenuAction>
                ) : null}
              </SidebarMenuItem>
            </SidebarMenu>
            {expanded ? (
              <SkillFileRows
                id={filesId}
                skill={skill}
                paths={expandedFiles}
                editedPaths={expandedEditedFiles}
                pending={expandedFilesPending}
                error={expandedFilesError}
                selectedPath={selected ? selectedFilePath : null}
                onSelect={onSelectFile}
                onRename={onRenameFile}
                onDelete={onDeleteFile}
                editable={key === editableFileKey}
              />
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}

function SkillFileRows({
  id,
  skill,
  paths,
  editedPaths,
  pending,
  error,
  selectedPath,
  onSelect,
  onRename,
  onDelete,
  editable,
}: {
  id: string;
  skill: SkillSummary;
  paths?: readonly string[];
  /** Files whose bytes differ from the live version. */
  editedPaths?: readonly string[];
  pending: boolean;
  error: boolean;
  selectedPath: string | null;
  onSelect: (skill: SkillSummary, path: string) => void;
  onRename: (skill: SkillSummary, path: string, nextPath: string) => void;
  onDelete: (skill: SkillSummary, path: string) => void;
  editable: boolean;
}) {
  const [editingPath, setEditingPath] = React.useState<string | null>(null);
  const [renameDraft, setRenameDraft] = React.useState('');
  const [renameError, setRenameError] = React.useState<string | null>(null);

  if (error) {
    return (
      <p id={id} className="ml-4 border-l py-1 pl-6 text-xs text-muted-foreground">
        Unable to load files.
      </p>
    );
  }
  if (pending || !paths) {
    return (
      <div id={id} className="ml-4 space-y-1 border-l py-1 pl-6" aria-busy="true">
        <Skeleton className="h-5 w-28" />
        <Skeleton className="h-5 w-36" />
      </div>
    );
  }
  return (
    <SidebarMenu id={id} className="ml-4 w-auto gap-px border-l py-1 pl-3">
      {paths.map((path) => {
        const selected = path === selectedPath;
        const canManage = editable && path !== RESERVED_SKILL_PATH;
        if (editingPath === path) {
          const commitRename = () => {
            const checked = normalizeSkillPath(renameDraft);
            if (!checked.ok) {
              setRenameError(checked.reason);
              return;
            }
            if (
              paths.some(
                (candidate) =>
                  candidate !== path && candidate.toLowerCase() === checked.path.toLowerCase(),
              )
            ) {
              setRenameError('Another file already uses that path.');
              return;
            }
            setEditingPath(null);
            setRenameError(null);
            onRename(skill, path, checked.path);
          };
          return (
            <SidebarMenuItem key={path} className="py-0.5">
              <form
                onSubmit={(event) => {
                  event.preventDefault();
                  commitRename();
                }}
              >
                <Input
                  value={renameDraft}
                  aria-label={`New path for ${path}`}
                  autoFocus
                  onChange={(event) => {
                    setRenameDraft(event.target.value);
                    setRenameError(null);
                  }}
                  onBlur={commitRename}
                  onKeyDown={(event) => {
                    if (event.key === 'Escape') {
                      event.preventDefault();
                      setEditingPath(null);
                      setRenameError(null);
                    }
                  }}
                  className="h-7 px-2 text-xs"
                />
                {renameError ? (
                  <p role="alert" className="px-1 pt-1 text-[11px] text-destructive">
                    {renameError}
                  </p>
                ) : null}
              </form>
            </SidebarMenuItem>
          );
        }
        return (
          <SidebarMenuItem key={path}>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              data-size="sm"
              data-active={selected}
              data-testid="skill-tree-file"
              aria-current={selected ? 'true' : undefined}
              onClick={() => onSelect(skill, path)}
              className={cn(
                'peer/menu-button h-7 w-full justify-start gap-2 px-2 text-left text-xs font-normal',
                canManage &&
                  'transition-[padding] group-hover/menu-item:pr-8 group-focus-within/menu-item:pr-8',
                selected ? 'bg-muted font-medium text-foreground' : 'text-muted-foreground',
              )}
            >
              <FileText className="size-3.5 shrink-0" aria-hidden />
              <span className="min-w-0 flex-1 truncate">{path}</span>
              {editedPaths?.includes(path) ? (
                <Hint label="Saved edits that are not live yet">
                  <span
                    data-testid="skill-tree-file-edited"
                    className="size-1.5 shrink-0 rounded-full bg-amber-500"
                    aria-hidden
                  />
                </Hint>
              ) : null}
            </Button>
            {canManage ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <SidebarMenuAction
                    showOnHover
                    className="text-muted-foreground peer-hover/menu-button:text-muted-foreground"
                    aria-label={`File options for ${path}`}
                  >
                    <MoreHorizontal aria-hidden />
                  </SidebarMenuAction>
                </DropdownMenuTrigger>
                <DropdownMenuContent side="right" align="start" className="w-40">
                  <DropdownMenuItem
                    onSelect={() => {
                      onSelect(skill, path);
                      setRenameDraft(path);
                      setRenameError(null);
                      setEditingPath(path);
                    }}
                  >
                    <Pencil className="size-4" />
                    Rename
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className="text-destructive focus:text-destructive"
                    onSelect={() => {
                      onSelect(skill, path);
                      onDelete(skill, path);
                    }}
                  >
                    <Trash2 className="size-4" />
                    Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : null}
          </SidebarMenuItem>
        );
      })}
    </SidebarMenu>
  );
}

function Muted({ children }: { children: React.ReactNode }) {
  return <p className="px-4 py-1 text-xs text-muted-foreground">{children}</p>;
}

/** The rounded search field the filter bar uses, without chips: name search only. */
export function SearchPill({
  label,
  placeholder,
  value,
  onChange,
  onEnter,
  className,
}: {
  label: string;
  placeholder: string;
  value: string;
  onChange: (next: string) => void;
  /** Enter in the box: run what the list would do first. */
  onEnter?: () => void;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'flex h-9 items-center gap-2 rounded-full border bg-background px-3 ring-offset-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2',
        className,
      )}
    >
      <Search className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
      <input
        type="search"
        aria-label={label}
        placeholder={placeholder}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={(event) => {
          if (event.key !== 'Enter' || !onEnter || event.nativeEvent.isComposing) return;
          event.preventDefault();
          onEnter();
        }}
        className="h-full min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
      />
    </div>
  );
}
